import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Heart, MapPin, Loader2, Eye, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import NotificationBell from '../components/NotificationBell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Likes() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [likers, setLikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealedIds, setRevealedIds] = useState(new Set());
  const [swiping, setSwiping] = useState(false);

  useEffect(() => {
    if (!user?.is_paid) {
      navigate('/paywall');
      return;
    }
    fetchLikes();
  }, [user, navigate]);

  const fetchLikes = async () => {
    try {
      const { data } = await axios.get(`${API}/likes/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikers(data.likers);
    } catch (e) {
      console.error('Failed to fetch likes:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = (id) => {
    setRevealedIds(prev => new Set([...prev, id]));
  };

  const handleLikeBack = async (likerId) => {
    if (swiping) return;
    setSwiping(true);
    try {
      const { data } = await axios.post(`${API}/swipe`, {
        target_user_id: likerId,
        action: 'like'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from list
      setLikers(prev => prev.filter(l => l._id !== likerId));

      if (data.is_match) {
        // Navigate to the chat with the new match
        navigate(`/app/chat/${data.match_id}`);
      }
    } catch (e) {
      console.error('Swipe error:', e);
    } finally {
      setSwiping(false);
    }
  };

  const handlePass = async (likerId) => {
    if (swiping) return;
    setSwiping(true);
    try {
      await axios.post(`${API}/swipe`, {
        target_user_id: likerId,
        action: 'pass'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikers(prev => prev.filter(l => l._id !== likerId));
    } catch (e) {
      console.error('Pass error:', e);
    } finally {
      setSwiping(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="px-6 py-5 flex items-center justify-between border-b border-zinc-900">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-white flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
            Who Liked You
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            {likers.length} {likers.length === 1 ? 'person likes' : 'people like'} you
          </p>
        </div>
        <NotificationBell />
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-4 pb-24 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
          </div>
        )}

        {!loading && likers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-zinc-700" />
            </div>
            <h2 className="font-outfit text-xl font-medium text-white mb-2">No Likes Yet</h2>
            <p className="text-zinc-500 text-sm mb-6 max-w-xs">
              When someone swipes right on you, they'll appear here. Keep your profile fresh to attract more likes!
            </p>
            <Button
              data-testid="go-discover-btn"
              onClick={() => navigate('/app')}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-full px-6"
            >
              Keep Swiping
            </Button>
          </div>
        )}

        {!loading && likers.length > 0 && (
          <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
            {likers.map((liker) => {
              const isRevealed = revealedIds.has(liker._id);
              const photo = liker.photos?.[0] || 'https://images.unsplash.com/photo-1694877164163-bd51561f794a?w=300';

              return (
                <div
                  key={liker._id}
                  data-testid={`liker-card-${liker._id}`}
                  className="relative rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 group"
                >
                  {/* Photo */}
                  <div className="relative aspect-[3/4]">
                    <img
                      src={photo}
                      alt={isRevealed ? liker.name : 'Someone likes you'}
                      className={`w-full h-full object-cover transition-all duration-500 ${
                        isRevealed ? '' : 'blur-xl scale-110'
                      }`}
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

                    {/* Blurred state - tap to reveal */}
                    {!isRevealed && (
                      <button
                        data-testid={`reveal-btn-${liker._id}`}
                        onClick={() => handleReveal(liker._id)}
                        className="absolute inset-0 flex flex-col items-center justify-center z-10"
                      >
                        <div className="w-14 h-14 rounded-full bg-rose-600/80 backdrop-blur-md flex items-center justify-center mb-2 shadow-[0_4px_20px_rgba(225,29,72,0.4)]">
                          <Eye className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-white text-sm font-medium">Tap to Reveal</span>
                      </button>
                    )}

                    {/* Like badge */}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-rose-600/90 backdrop-blur-sm flex items-center gap-1">
                      <Heart className="w-3 h-3 text-white fill-white" />
                      <span className="text-white text-xs font-semibold">Liked you</span>
                    </div>

                    {/* User info (shown when revealed) */}
                    <div className={`absolute bottom-0 left-0 right-0 p-3 transition-opacity duration-300 ${isRevealed ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-outfit font-semibold text-white text-base truncate">
                          {liker.name}, {liker.age}
                        </h3>
                        {liker.verified && <ShieldCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                      </div>
                      {liker.distance !== undefined && (
                        <div className="flex items-center gap-1 text-zinc-400 mt-0.5">
                          <MapPin className="w-3 h-3" />
                          <span className="text-xs">{liker.distance} mi</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action buttons (shown when revealed) */}
                  {isRevealed && (
                    <div className="flex border-t border-zinc-800">
                      <button
                        data-testid={`pass-liker-${liker._id}`}
                        onClick={() => handlePass(liker._id)}
                        disabled={swiping}
                        className="flex-1 flex items-center justify-center gap-1.5 py-3 text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors border-r border-zinc-800"
                      >
                        <span className="text-sm">Pass</span>
                      </button>
                      <button
                        data-testid={`like-back-${liker._id}`}
                        onClick={() => handleLikeBack(liker._id)}
                        disabled={swiping}
                        className="flex-1 flex items-center justify-center gap-1.5 py-3 text-rose-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                      >
                        <Heart className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">Like Back</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <BottomNav active="likes" />
    </div>
  );
}
